/**
 * @file src/core/lessonData.js
 * Đọc cấu trúc bài học TỪ CHÍNH FILE .glb, và gộp nó với file chữ.
 *
 * KHÔNG dùng `node:*` — module này chạy được ở CẢ HAI phía:
 *   • `tools/gen-binding.mjs`  — sinh JSON lúc build
 *   • `src/dashboard.js`       — người dùng ném file vào trình duyệt, dựng ngay tại chỗ
 * Hai phía phải cho ra CÙNG một kết quả, nên luật đọc chỉ được viết đúng một lần ở đây.
 * Trước khi tách ra, luật này nằm trong CLI; bản dashboard mà chép lại thì hai bên sẽ
 * trôi khỏi nhau lúc nào không biết, và bài xem thử trên dashboard khác bài build ra.
 */

/** Tiền tố node cảnh. 'sence' là lỗi chính tả của các model dựng trước, vẫn đọc được. */
export const SCENE_PREFIXES = ['scene', 'sence'];
/** Tiền tố node CHI TIẾT chú thích. */
const DESC_PREFIXES = ['des'];
/** Tiền tố node CHÚ THÍCH NHỎ. Tên node cũng chính là khoá bên file chữ. */
const TITLE_PREFIXES = ['title'];
/** Node mang thông tin của bước. KHÔNG bắt buộc — chỗ chính thức của `TT` là file chữ. */
const INFO_PREFIX = 'TT';
/** Tên bài, node đặt NGOÀI mọi nhóm scene. Cũng chỉ là dự phòng. */
const LESSON_TITLE = 'TB';
/** Bản gộp node + chữ — thứ đẩy lên CMS, gọi ngược về, và là thứ bài đọc TRƯỚC TIÊN. */
export const MERGED_FILE = 'lesson.json';

const low = (/** @type {any} */ s) =>
  String(s ?? '')
    .toLowerCase()
    .replace(/[\s_]+/g, ' ')
    .trim();

/** Khung ô chữ RỖNG. Ghi sẵn để người viết nội dung nhìn là biết phải điền chỗ nào. */
export const Ô = () => ({ vi: '', en: '' });
/** Trả về chính ô đó nếu có chữ thật, ngược lại null. Khung rỗng KHÔNG tính là có. */
export const có = (/** @type {any} */ v) => (v && (v.vi || v.en) ? v : null);
/** Nhận cả `"Tên bước"` lẫn `{vi, en}`. Chuỗi trần dùng chung cho cả hai ngôn ngữ. */
export const ôHoặcChuỗi = (/** @type {any} */ v) =>
  typeof v === 'string' && v.trim() ? { vi: v, en: v } : có(v);

/** Chuỗi vi/en trong extras của một node. null nếu không có. */
const lang = (/** @type {any} */ n) => {
  const e = n?.extras;
  if (!e) return null;
  /** @type {Record<string, string>} */
  const o = {};
  for (const k of Object.keys(e)) {
    if (/^(vi|en)$/i.test(k) && typeof e[k] === 'string') o[k.toLowerCase()] = e[k];
  }
  return Object.keys(o).length ? { vi: o.vi ?? null, en: o.en ?? null } : null;
};

/**
 * KHOÁ GHÉP DO BÊN 3D ĐẶT — đọc từ TÊN THUỘC TÍNH trong extras của node.
 *
 * Trong Blender, node `title1` có Custom Properties:
 *     name  = "title1"
 *     CT1   = ""            ← TÊN thuộc tính này chính là khoá
 * Node `des1` đi kèm mang `CTC1`. Chuỗi đó là thứ tra sang file chữ.
 *
 * Đọc TÊN thuộc tính chứ không phải giá trị: giá trị thường để rỗng, bên 3D chỉ tạo
 * thuộc tính để đánh dấu. Vẫn xét thêm giá trị làm đường lui, phòng model gán ngược lại.
 *
 * Không có thuộc tính nào như vậy → trả null, bên gọi rơi về dùng TÊN NODE làm khoá, để
 * model dựng trước đây vẫn mở được.
 *
 * @param {any} n node glTF @returns {string|null} khoá viết hoa, hoặc null
 */
const khoáTừExtras = (n, dạng = /^CTC?\s*\d+$/i) => {
  const e = n?.extras;
  if (!e) return null;
  const hợpLệ = (/** @type {any} */ x) => {
    const t = String(x ?? '').trim();
    return dạng.test(t) ? t.replace(/\s+/g, '').toUpperCase() : null;
  };
  for (const k of Object.keys(e)) {
    const khoá = hợpLệ(k);
    if (khoá) return khoá;
  }
  for (const v of Object.values(e)) {
    if (typeof v !== 'string') continue;
    const khoá = hợpLệ(v);
    if (khoá) return khoá;
  }
  return null;
};

/** Chỉ `CT<số>` — KHÔNG khớp `CTC1` (sau `CT` là chữ C chứ không phải số). */
const DẠNG_CT = /^CT\s*\d+$/i;
/** Chỉ `CTC<số>`. */
const DẠNG_CTC = /^CTC\s*\d+$/i;
/** Phần số của khoá: `CTC12` → `12`. Dùng để ghép CT ↔ CTC. */
const sốKhoá = (/** @type {string | null} */ k) => (String(k ?? '').match(/\d+$/) ?? [''])[0];

/** Phần đuôi sau tiền tố khớp được: "des21" → "21". Dùng để ghép chú thích ↔ chi tiết. */
const suffix = (/** @type {string} */ name, /** @type {string[]} */ pres) => {
  const t = low(name);
  for (const p of pres) {
    const q = low(p);
    if (t.startsWith(q)) return t.slice(q.length).replace(/^[\s._-]+/, '');
  }
  return t;
};
/**
 * Rút gọn id bước để so sánh: bỏ mọi ký tự không phải chữ/số.
 * `scene-1` ↔ `scene1` ↔ `Scene 1` ↔ `sence_1` — file chữ viết tay và id sinh từ model
 * hay lệch nhau đúng một dấu gạch, mà lệch là TOÀN BỘ chữ của bước rơi mất trong im lặng.
 * @param {any} v
 */
const idGọn = (v) =>
  String(v ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const hasPre = (/** @type {string} */ n, /** @type {string[]} */ pres) =>
  pres.some((p) => low(n).startsWith(low(p)));

// PHẢI có SỐ ngay sau tiền tố. Không có phép thử này thì node bọc ngoài "Scene Collection"
// cũng khớp "scene" và bị đếm thành một cảnh — đã mắc đúng lỗi đó: 4 cảnh thành 5, và
// cảnh thừa gom hết 18 panel của cả model.
const isScene = (/** @type {string} */ n) =>
  SCENE_PREFIXES.some((p) => new RegExp(`^${p}[\\s._-]*\\d+`, 'i').test(low(n)));

/**
 * TÊN BƯỚC mà một node tham chiếu trong `scenehide*` trỏ tới.
 *
 * Lấy từ EXTRAS trước, tên node chỉ là phương án chót. Lý do: Blender bắt tên trong file
 * phải duy nhất, nên bản sao tham chiếu của "scene 2" bao giờ cũng thành "scene 2.001" —
 * tên node không bao giờ trùng khít tên bước thật được. Bên dựng model vì vậy gõ đúng tên
 * bước vào một Custom Property, và ĐÓ mới là nguồn sự thật.
 *
 * Không đòi một khoá extras cố định: quét mọi giá trị, cái nào trông như tên bước
 * (`isScene`) thì lấy. Ép một khoá duy nhất là lại thêm một chỗ gõ sai mà không ai báo.
 *
 * @param {any} n node tham chiếu
 * @returns {string | null}
 */
export function tênBướcThamChiếu(n) {
  for (const v of Object.values(n?.extras ?? {})) {
    if (typeof v === 'string' && isScene(v.trim())) return v.trim();
  }
  const nm = String(n?.extras?.name ?? '').trim();
  if (nm) return nm;
  // Hậu tố `.001` do Blender thêm khi trùng tên — cắt đi để còn khớp được tên bước.
  const tự = String(n?.name ?? '').replace(/\.\d{3}$/, '');
  return tự || null;
}

// Tên clip: <tiền tố><số bước>[_<loại>]_<tên người đọc>
//   scene2_datmat   → bước 2, loại 1 (không có số loại → mặc định)
//   scene1_2_matdat → bước 1, loại 2 (theo tiến trình)
const CLIP_RE = /^(?:scene|sence|anim)[\s._-]*(\d+)(?:[\s._-]+(\d+))?/;

/**
 * Đọc chunk JSON của một file .glb. Không giải mã hình học — chỉ cần cây node, extras và
 * danh sách animation, nên đọc được cả file vài trăm MB trong tích tắc.
 * @param {ArrayBuffer | Uint8Array} data
 * @returns {any} đối tượng glTF
 */
export function đọcGlb(data) {
  const buf = data instanceof Uint8Array ? data : new Uint8Array(data);
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  if (buf.byteLength < 12 || dv.getUint32(0, true) !== 0x46546c67)
    throw new Error('Không phải file .glb (thiếu chữ ký glTF)');
  let off = 12;
  while (off + 8 <= buf.byteLength) {
    const len = dv.getUint32(off, true);
    const type = dv.getUint32(off + 4, true);
    if (type === 0x4e4f534a)
      return JSON.parse(new TextDecoder().decode(buf.subarray(off + 8, off + 8 + len)));
    off += 8 + len + ((4 - (len % 4)) % 4);
  }
  throw new Error('Không đọc được chunk JSON của .glb');
}

/**
 * Quét cây node, dựng BẢN ĐỒ NODE của từng bước + gom chữ nháp có sẵn trong extras.
 *
 * @param {any} g đối tượng glTF (từ đọcGlb)
 * @returns {{scenes: any[], chữCảnh: Map<string, any>, TB: any, cảnhBáo: string[]}}
 *   `scenes` là phần `scenes[]` của model.binding.json;
 *   `chữCảnh` là sceneId → các ô chữ đọc được từ extras, khoá theo tên node.
 */
export function quétModel(g) {
  /** @type {any[]} */
  const bScenes = [];
  /** Lỗi KHAI BÁO bên model — không chặn được việc sinh file, nhưng phải in ra. */
  /** @type {string[]} */
  const cảnhBáo = [];
  /** @type {Map<string, any>} */
  const chữCảnh = new Map();

  const clipDuration = (/** @type {any} */ a) => {
    let d = 0;
    for (const sp of a.samplers ?? []) {
      const acc = g.accessors?.[sp.input];
      if (acc?.max?.[0] != null) d = Math.max(d, acc.max[0]);
    }
    return Math.round(d * 1000) / 1000;
  };
  const clips = (g.animations ?? []).map((/** @type {any} */ a) => {
    const m = CLIP_RE.exec(low(a.name));
    return { name: a.name, num: m?.[1] ?? null, type: m?.[2] ?? '1', duration: clipDuration(a) };
  });

  // Node nằm TRONG `scenehide*` chỉ là TÊN THAM CHIẾU tới bước khác, không phải bước.
  // Blender bắt tên trong file phải duy nhất, nên bản sao tham chiếu của "scene2" ra
  // file là `scene2.001` — mà `isScene` vẫn khớp. Không loại chúng ra thì mỗi tham
  // chiếu đẻ thêm một bước ma trên thanh bước, mang tên trùng bước thật.
  /** @type {Set<number>} */
  const thamChiếu = new Set();
  (g.nodes ?? []).forEach((/** @type {any} */ n) => {
    if (!low(n.name ?? '').startsWith('scenehide')) return;
    const đệ = (/** @type {number} */ i) => {
      thamChiếu.add(i);
      for (const c of g.nodes[i]?.children ?? []) đệ(c);
    };
    for (const c of n.children ?? []) đệ(c);
  });

  for (const sc of (g.nodes ?? []).filter(
    (/** @type {any} */ n, /** @type {number} */ i) => isScene(n.name ?? '') && !thamChiếu.has(i),
  )) {
    /** @type {any[]} */ const titles = [];
    /** @type {any[]} */ const descs = [];
    /** @type {any} */ let cam = null;
    /** @type {any} */ let tgt = null;
    /** @type {any} */ let status = null;
    /** @type {any} */ let info = null;
    const rec = (/** @type {number} */ i) => {
      const n = g.nodes[i];
      const nm = n.name ?? '';
      if (!cam && /^camera/i.test(nm)) cam = n;
      else if (!tgt && /^target/i.test(nm)) tgt = n;
      else if (!status && /^status/i.test(nm)) status = n;
      // Chi tiết xét TRƯỚC chú thích: tiền tố nào là khúc đầu của tiền tố khác thì phải
      // đứng trước, không thì node chi tiết bị gom nhầm vào danh sách chú thích.
      else if (hasPre(nm, DESC_PREFIXES)) descs.push(n);
      else if (hasPre(nm, TITLE_PREFIXES)) titles.push(n);
      else if (low(nm).startsWith(low(INFO_PREFIX))) info = n;
      for (const c of n.children ?? []) rec(c);
    };
    for (const c of sc.children ?? []) rec(c);
    titles.sort((a, b) => low(a.name).localeCompare(low(b.name), 'en', { numeric: true }));

    // GHÉP chú thích ↔ chi tiết theo HAI đường, ưu tiên đường KHOÁ.
    //
    // Vì sao không chỉ so hậu tố tên node như trước: khoá `CT<n>` được đánh LẠI TỪ 1 cho
    // mỗi bước, độc lập hoàn toàn với tên node. Model thật có node `title40` mang khoá
    // `CT1` — so hậu tố thì đi tìm `des40`, trong khi chi tiết của nó nằm ở node mang
    // `CTC1`, tên là gì cũng được. Ghép trượt là cả cột chi tiết rơi mất, im lặng.
    /** @type {Map<string, any>} */
    const dTheoKhoá = new Map();
    /** @type {Map<string, any>} */
    const dTheoTên = new Map();
    for (const d of descs) {
      const k = khoáTừExtras(d, DẠNG_CTC);
      if (k) dTheoKhoá.set(sốKhoá(k), d);
      dTheoTên.set(suffix(d.name, DESC_PREFIXES), d);
    }
    // ID CỦA BƯỚC = ĐÚNG TÊN NODE trong Blender. Node tên `scene1` thì file chữ ghi
    // `"id": "scene1"` — nhìn Outliner là biết phải gõ gì, không phải đoán xem hệ thống
    // biến đổi tên ra sao.
    // (Lúc GHÉP chữ vẫn so id đã rút gọn — xem idGọn — nên file lỡ ghi `scene-1` cho node
    // `scene1` thì vẫn nhận, không im lặng làm rơi cả bước.)
    const id = sc.name;

    /** @type {any[]} */
    const bTexts = [];
    /** Đếm node ĐÃ gán khoá và gom node CHƯA gán, để báo chỗ khai nửa vời — xem dưới. */
    let sốCóKhoá = 0;
    /** @type {string[]} */
    const thiếuKhoá = [];
    /** TT đứng đầu — nó là đoạn dẫn cho cả bước, đọc sau các chú thích lẻ thì mất mạch. */
    /** @type {Record<string, any>} */
    const ôCảnh = { TT: lang(info) ?? Ô() };
    titles.forEach((t) => {
      // KHOÁ do bên 3D gán trong extras (`CT1` trên node title, `CTC1` trên node des).
      // Hai dạng xét RIÊNG: nhận lẫn lộn thì node title lỡ mang `CTC3` sẽ ghi chữ của
      // viên chú thích vào đúng ô "chi tiết" của file chữ, mà không có gì báo.
      const kCTgán = khoáTừExtras(t, DẠNG_CT);
      const d =
        (kCTgán ? dTheoKhoá.get(sốKhoá(kCTgán)) : null) ??
        dTheoTên.get(suffix(t.name, TITLE_PREFIXES)) ??
        null;
      const point = (t.children ?? [])
        .map((/** @type {number} */ c) => g.nodes[c])
        .find((/** @type {any} */ c) => /^point/i.test(c.name ?? ''));

      // Model chưa gán khoá thì dùng TÊN NODE — các bản dựng trước vẫn mở được.
      const kCTCgán = d ? khoáTừExtras(d, DẠNG_CTC) : null;
      const kCT = kCTgán ?? t.name;
      const kCTC = d ? (kCTCgán ?? d.name) : null;

      // ── Soát khai báo, gom lại để gen-binding in ra ─────────────────────
      if (!kCTgán && khoáTừExtras(t, DẠNG_CTC))
        cảnhBáo.push(`${t.name}: mang khoá CTC — node title phải mang CT`);
      if (d && !kCTCgán && khoáTừExtras(d, DẠNG_CT))
        cảnhBáo.push(`${d.name}: mang khoá CT — node des phải mang CTC`);
      if (kCTgán && kCTCgán && sốKhoá(kCTgán) !== sốKhoá(kCTCgán))
        cảnhBáo.push(`${t.name} (${kCTgán}) ghép với ${d.name} (${kCTCgán}) — số khoá lệch`);
      // KHÔNG cảnh báo khi thiếu node `des`: viên chỉ có tiêu đề, không có chi tiết là
      // chuyện BÌNH THƯỜNG và do bên dựng model quyết. Lúc chạy nó thành nhãn thuần tuý,
      // không dấu sao, bấm vào không mở gì — đúng như ý, không phải lỗi.
      if (!point) cảnhBáo.push(`${t.name}: không có node con tên point* — viên mất điểm neo`);
      if (kCTgán) sốCóKhoá++;
      else thiếuKhoá.push(t.name);
      bTexts.push({
        ct: kCT,
        ctc: kCTC,
        titleNode: t.name,
        descNode: d?.name ?? null,
        point: point?.name ?? null,
      });
      ôCảnh[kCT] = lang(t) ?? Ô();
      if (d && kCTC) ôCảnh[kCTC] = lang(d) ?? Ô();
    });

    // KHAI NỬA VỜI trong cùng một bước: vài node có `CT`/`CTC`, vài node không. Node
    // không có khoá rơi về dùng TÊN NODE làm khoá, nên file chữ phải có đúng một ô tên
    // `title32222` mới nhận được chữ — mà bên biên tập viết theo `CT<n>` thì không đời
    // nào có ô đó. Kết quả là mấy viên đó câm lặng, không có gì báo. Bước nào KHÔNG gán
    // khoá cho node nào thì bỏ qua: đó là model dựng theo lối cũ, hoàn toàn hợp lệ.
    const desThiếu = descs
      .filter((/** @type {any} */ x) => !khoáTừExtras(x, DẠNG_CTC))
      .map((/** @type {any} */ x) => x.name);
    // MỘT dòng cho cả bước, gộp title lẫn des: chúng luôn thiếu theo cụm (một cặp viên
    // sót lại từ bản dựng trước thì thiếu cả hai), tách hai dòng chỉ làm dài báo cáo.
    const sót = [...thiếuKhoá, ...desThiếu];
    if (sốCóKhoá && sót.length) {
      cảnhBáo.push(
        `${sc.name}: ${sót.length} node chưa gán khoá trong khi ${sốCóKhoá} node khác đã có` +
          ` (${sót.join(', ')})`,
      );
    }

    /** @type {any} */
    const b = { id, node: sc.name, camera: { eye: cam?.name ?? null, target: tgt?.name ?? null } };
    if (status) {
      // Theo TIỀN TỐ, không khớp tên tuyệt đối: bên Blender các nhóm này đánh số theo
      // bước (`hide1`, `show2`…). Trả về MẢNG vì một `statusN` có thể có nhiều nhóm
      // cùng loại; tiết đọc mảng này rồi ẩn/hiện hết một lượt.
      const con = (/** @type {any} */ n) =>
        (n?.children ?? []).map((/** @type {number} */ c) => g.nodes[c]);
      const đám = (/** @type {any} */ n, /** @type {string} */ pre) =>
        con(n).filter((/** @type {any} */ c) => low(c.name ?? '').startsWith(pre));
      const tên = (/** @type {any[]} */ ns) => ns.map((/** @type {any} */ c) => c.name);

      const nhómHide = đám(status, 'hide');
      // BÊN TRONG mỗi `hide*` có hai loại con:
      //   · `model*`     → mesh phải ẩn. Không cần xử lý riêng: cả nhóm `hide*` được mờ
      //                    một lượt nên mesh nằm sâu bên trong cũng đi theo. Ghi tên ra
      //                    đây để nhìn JSON là biết bên Blender gom những gì.
      //   · `scenehide*` → CON của nó mang TÊN CÁC BƯỚC phải ẩn khi đang ở bước này.
      //                    Chúng là node rỗng dùng làm danh sách tham chiếu, bản thân
      //                    chúng không phải thứ bị ẩn.
      const models = nhómHide.flatMap((/** @type {any} */ h) => tên(đám(h, 'model')));
      // Tên bước lấy từ EXTRAS của node tham chiếu — xem tênBướcThamChiếu().
      const cảnhẨn = nhómHide.flatMap((/** @type {any} */ h) =>
        đám(h, 'scenehide').flatMap((/** @type {any} */ sh) =>
          con(sh)
            .map((/** @type {any} */ c) => tênBướcThamChiếu(c))
            .filter((/** @type {string | null} */ t) => Boolean(t)),
        ),
      );
      b.cutaway = {
        node: status.name,
        hide: tên(nhómHide),
        show: tên(đám(status, 'show')),
        ...(models.length ? { models } : {}),
        ...(cảnhẨn.length ? { hideScenes: cảnhẨn } : {}),
        glassToken: 'glass',
      };
    }
    // Clip của bước này (nếu có) — nhìn JSON là biết bước nào có animation, loại gì.
    const num = (low(sc.name).match(/(\d+)\s*$/) ?? [])[1] ?? '';
    const mine = num ? clips.filter((/** @type {any} */ c) => c.num === num) : [];
    if (mine.length) {
      const stage = mine.find((/** @type {any} */ c) => c.type === '2');
      b.animation = {
        clip: (stage ?? mine[0]).name,
        type: Number(stage ? 2 : 1),
        duration: (stage ?? mine[0]).duration,
      };
      if (mine.length > 1) b.animation.also = mine.slice(1).map((/** @type {any} */ c) => c.name);
    }
    b.texts = bTexts;
    b.tt = 'TT';
    b.name = lang(sc) ?? null;
    bScenes.push(b);
    chữCảnh.set(id, ôCảnh);
  }

  /** @type {any} */
  let tbNode = null;
  for (const n of g.nodes ?? []) if (low(n.name ?? '') === low(LESSON_TITLE)) tbNode = tbNode ?? n;

  return { scenes: bScenes, chữCảnh, TB: lang(tbNode), cảnhBáo };
}

/**
 * Gom chữ của MỘT file (bản gộp hoặc file chữ) và quy hết về TÊN NODE.
 *
 * Quy về tên node chứ không giữ theo khoá, vì khoá `CT1` đánh lại từ 1 trong mỗi bước —
 * nó đổi nghĩa ngay khi có ai chèn thêm một chú thích vào giữa. Ghép theo khoá lúc đó là
 * gán chữ nhầm hàng loạt mà không có gì báo. Tên node thì đứng yên.
 *
 * @param {any} d file chữ hoặc bản gộp
 * @param {any} bindCũ bản đồ node của LẦN SINH TRƯỚC (để dịch khoá cũ ra tên node)
 * @param {{theoNode: Map<string, any>, TT: Map<string, any>, tên: Map<string, any>,
 *          panel: Map<string, any>, TB: {v: any}}} thùng nơi gom vào
 */
export function gomChữ(d, bindCũ, thùng) {
  if (!d) return;
  /** `<id bước rút gọn> <khoá>` → node chú thích. CÓ ràng buộc bước. */
  const nodeTheoKhoá = new Map();
  /**
   * TÊN NODE (`title21`, `des21`) → node chú thích. KHÔNG ràng buộc bước, và chỉ được
   * chứa TÊN NODE.
   *
   * Tuyệt đối KHÔNG nhét khoá `CT<n>`/`CTC<n>` vào đây — đó là lỗi đã gây mất chữ thật:
   * khoá được đánh LẠI TỪ 1 ở mỗi bước nên `CT1` của bước 1, 2, 3, 4 đều ghi đè lên nhau,
   * bước cuối thắng. Kết quả: "Cửa thông khí" khai ở bước 3 lại hiện ở bước 4, còn bước 3
   * thì trống. Tên node thì Blender bắt duy nhất trong cả file nên để chung được.
   */
  const theoTênNode = new Map();
  for (const bs of bindCũ?.scenes ?? [])
    for (const t of bs.texts ?? []) {
      // Cất theo CẢ hai khoá của cặp. `CTC<n>` cũng trỏ về node chú thích — chỗ rót phân
      // biệt chú thích/chi tiết bằng cờ `làChiTiết`, không bằng node.
      for (const k of [t.ct, t.ctc, t.id])
        if (k) nodeTheoKhoá.set(`${idGọn(bs.id)} ${low(k)}`, t.titleNode ?? null);
      if (t.titleNode) theoTênNode.set(low(t.titleNode), { node: t.titleNode, chiTiết: false });
      if (t.descNode) theoTênNode.set(low(t.descNode), { node: t.titleNode, chiTiết: true });
    }
  const nodeCảnhTheoId = new Map(
    (bindCũ?.scenes ?? []).map((/** @type {any} */ b) => [idGọn(b.id), b.node]),
  );

  thùng.TB.v ??= có(d.lesson?.TB) ?? có(d.lesson?.title) ?? có(d.TB);

  for (const sc of d.scenes ?? []) {
    const nodeCảnh = sc.node ?? nodeCảnhTheoId.get(idGọn(sc.id)) ?? sc.id;
    const số = String(sc.id ?? '').match(/(\d+)/)?.[1];
    // Cất dưới CẢ HAI khoá: id bước đã rút gọn, và tên node cảnh. File chữ thường chỉ có
    // `id`, còn bên rót chữ lại cầm bản đồ node — thiếu một trong hai là hụt.
    const khoáCảnh = [idGọn(sc.id), low(nodeCảnh)].filter(Boolean);
    const cất = (/** @type {Map<string, any>} */ m, /** @type {any} */ v) => {
      for (const k of khoáCảnh) if (!m.has(k)) m.set(k, v);
    };
    // `TT` (bản này) · `TT<số>` (bản phẳng) · `info` (bản đầu).
    const tt = có(sc.TT) ?? có(sc[`TT${số}`]) ?? có(sc.info) ?? có(d[`TT${số}`]);
    if (tt) cất(thùng.TT, tt);
    const tên = ôHoặcChuỗi(sc.name) ?? ôHoặcChuỗi(sc.title);
    if (tên) cất(thùng.tên, tên);
    // `panel: false` — bước này hiện chú thích dưới dạng CHỮ TRẦN, không có viên nền.
    // Chỉ nhận khi khai TƯỜNG MINH: thiếu trường thì giữ mặc định có viên, đừng suy diễn.
    if (typeof sc.panel === 'boolean') cất(thùng.panel, sc.panel);

    for (const t of sc.texts ?? []) {
      const khoá = String(t.id ?? t.ct ?? '');
      const node =
        t.titleNode ??
        nodeTheoKhoá.get(`${idGọn(sc.id)} ${low(khoá)}`) ??
        theoTênNode.get(low(khoá))?.node ??
        null;
      if (!node) continue;
      const ô = thùng.theoNode.get(node) ?? {};
      ô.chú ??= có(t.title) ?? có(t.CT);
      ô.chiTiết ??= có(t.desc) ?? có(t.CTC);
      thùng.theoNode.set(node, ô);
    }
    for (const nguồn of [sc, d]) {
      for (const [k, v] of Object.entries(nguồn)) {
        if (!có(v)) continue;
        // Khoá là TÊN NODE. Vài file trung gian từng đánh khoá `CT<i>` — vẫn nhận, để
        // ai đã viết theo lối đó không mất chữ.
        const m = /^(CTC|CT)(\d*)$/i.exec(k);
        // KHOÁ `CT<n>`/`CTC<n>` tra bảng CÓ RÀNG BUỘC BƯỚC, và tra TRƯỚC bảng tên node.
        // Đảo thứ tự này là chữ chảy sang bước khác — xem chú thích ở theoTênNode.
        const theoKhoá = m
          ? (nodeTheoKhoá.get(`${idGọn(sc.id)} ${low(k)}`) ??
            nodeTheoKhoá.get(`${idGọn(sc.id)} ct${m[2]}`))
          : null;
        const theoTên = theoKhoá ? null : theoTênNode.get(low(k));
        const node = theoKhoá ?? theoTên?.node ?? null;
        if (!node) continue;
        const làChiTiết = theoTên ? theoTên.chiTiết : /^CTC$/i.test(String(m?.[1]));
        const ô = thùng.theoNode.get(node) ?? {};
        if (làChiTiết) ô.chiTiết ??= v;
        else ô.chú ??= v;
        thùng.theoNode.set(node, ô);
      }
    }
  }
}

/** Thùng rỗng cho gomChữ(). */
export const thùngRỗng = () => ({
  theoNode: new Map(),
  TT: new Map(),
  tên: new Map(),
  /** sceneId/tên node → `panel` của bước (false = chú thích chỉ có chữ, không có viên). */
  panel: new Map(),
  TB: { v: /** @type {any} */ (null) },
});

/**
 * Rót chữ đã gom vào đúng ô của bản đồ node MỚI.
 *
 * SỐ BƯỚC LUÔN THEO MODEL. File chữ thiếu bước nào thì bước đó vẫn còn đủ trong bài, chỉ
 * là không có tên và không có chữ — thà hiện một bước trống còn hơn nuốt mất nó, vì mất
 * bước là mất cả góc nhìn lẫn lớp cắt mà không ai nhận ra.
 *
 * @param {any[]} scenes bản đồ node mới (từ quétModel)
 * @param {Map<string, any>} chữCảnh chữ nháp đọc từ extras
 * @param {ReturnType<typeof thùngRỗng>} thùng chữ gom từ file
 */
export function rótChữ(scenes, chữCảnh, thùng) {
  for (const b of scenes) {
    const ô = chữCảnh.get(b.id);
    if (!ô) continue;
    const tra = (/** @type {Map<string, any>} */ m) => m.get(idGọn(b.id)) ?? m.get(low(b.node));
    const tt = tra(thùng.TT);
    if (tt) ô.TT = tt;
    const tên = tra(thùng.tên);
    if (tên) b.name = tên;
    const panel = tra(thùng.panel);
    if (typeof panel === 'boolean') b.panel = panel;
    for (const t of b.texts) {
      const cũ = thùng.theoNode.get(t.titleNode);
      if (!cũ) continue;
      if (cũ.chú) ô[t.ct] = cũ.chú;
      if (cũ.chiTiết && t.ctc) ô[t.ctc] = cũ.chiTiết;
    }
  }
}

/**
 * Dựng FILE CHỮ: `TB` trong khối `lesson`, các ô còn lại nhóm theo từng bước.
 * @param {any} hồSơBài @param {any} TB @param {any[]} scenes @param {Map<string, any>} chữCảnh
 */
export function dựngFileChữ(hồSơBài, TB, scenes, chữCảnh) {
  return {
    schemaVersion: 1,
    lesson: { ...hồSơBài, TB: TB ?? Ô() },
    scenes: scenes.map((b) => ({
      id: b.id,
      name: b.name ?? Ô(),
      ...(b.panel === false ? { panel: false } : {}),
      ...(chữCảnh.get(b.id) ?? {}),
    })),
  };
}

/**
 * Dựng BẢN GỘP: bản đồ node + chữ trong một file. Đây là thứ đẩy lên CMS và gọi ngược về.
 * @param {any} binding @param {any} lesson @param {Map<string, any>} chữCảnh
 */
export function dựngBảnGộp(binding, lesson, chữCảnh) {
  return {
    ...binding,
    lesson,
    scenes: binding.scenes.map((/** @type {any} */ b) => ({
      id: b.id,
      node: b.node,
      name: b.name ?? Ô(),
      ...(b.panel === false ? { panel: false } : {}),
      camera: b.camera,
      ...(b.cutaway ? { cutaway: b.cutaway } : {}),
      ...(b.animation ? { animation: b.animation } : {}),
      TT: chữCảnh.get(b.id)?.TT ?? Ô(),
      // TRA CHỮ BẰNG KHOÁ (`t.ct` / `t.ctc`), KHÔNG bằng tên node.
      //
      // `chữCảnh` được cất theo KHOÁ — `ôCảnh[kCT] = …` trong quétModel. Bản trước tra
      // bằng `t.titleNode`, và nó chỉ đúng KHI model chưa gán khoá: lúc đó khoá RƠI VỀ
      // chính tên node nên hai thứ trùng nhau. Model đã gán `CT1` thì `chữCảnh['title3']`
      // là undefined → mọi ô chữ ra rỗng → tiết thấy chú thích không có tiêu đề và BỎ QUA
      // hết (xem buildAnnotations: `if (!note.title) continue`). Triệu chứng: dựng bài
      // xong, bước nào cũng đủ camera, đủ lớp cắt, mà KHÔNG MỘT VIÊN CHÚ THÍCH NÀO hiện.
      //
      // `id` cũng lấy khoá: bên tiết tra `cTexts.get(t.ct ?? t.id)`, để tên node lại là
      // một lần lệch nữa ở tầng dưới.
      texts: b.texts.map((/** @type {any} */ t) => ({
        id: t.ct ?? t.titleNode,
        ct: t.ct,
        ctc: t.ctc,
        titleNode: t.titleNode,
        descNode: t.descNode,
        point: t.point,
        title: chữCảnh.get(b.id)?.[t.ct] ?? Ô(),
        desc: t.ctc ? (chữCảnh.get(b.id)?.[t.ctc] ?? Ô()) : null,
      })),
    })),
  };
}

/** Thông số render mặc định — dev tinh chỉnh bằng mắt, model không suy ra được. */
export const VIEW_MẶC_ĐỊNH = Object.freeze({
  fov: 42,
  near: 0.1,
  far: 400,
  flyMs: 2500,
  minElevationDeg: 2,
  zoomRange: { min: 0.5, max: 1.8 },
});
export const GLASS_MẶC_ĐỊNH = Object.freeze({
  opacity: 0.12,
  roughness: 0.03,
  metalness: 0,
  envIntensity: 2,
  emissive: '#ffffff',
  emissiveIntensity: 0.3,
  edge: { color: '#ffffff', opacity: 0.6, thresholdDeg: 12 },
});
